import React, { useState, useCallback } from 'react';
import { Upload, FileText, X, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';
import PaymentModal from './PaymentModal';
import { trackUploadComplete, trackError } from '../utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const UploadZone = ({ onUploadComplete }) => {
  const [dragActive, setDragActive] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [uploadedContractId, setUploadedContractId] = useState(null);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  }, []);

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (uploadedFile) => {
    const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    const maxSize = 10 * 1024 * 1024; // 10MB

    if (!validTypes.includes(uploadedFile.type)) {
      setError('Formato file non supportato. Usa PDF, JPG o PNG.');
      return;
    }

    if (uploadedFile.size > maxSize) {
      setError('File troppo grande. Massimo 10MB.');
      return;
    }

    setFile(uploadedFile);
    setError(null);
  };

  const removeFile = () => {
    setFile(null);
    setError(null);
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    setError(null);
    
    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('report_type', 'basic'); // Will be selected in modal
      
      // Upload to backend
      const response = await axios.post(`${BACKEND_URL}/api/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        // Track successful upload
        trackUploadComplete(file.type, file.size);
        
        // Show payment modal instead of redirect
        setUploadedContractId(response.data.contract_id);
        setShowPaymentModal(true);
        setUploading(false);
        
        if (onUploadComplete) {
          onUploadComplete(response.data);
        }
      }
    } catch (err) {
      console.error('Upload error:', err);
      const errorMsg = err.response?.data?.detail || 'Errore durante il caricamento. Riprova.';
      setError(errorMsg);
      setUploading(false);
      
      // Track error
      trackError('upload_failed', errorMsg);
    }
  };

  return (
    <>
      <div className="w-full max-w-2xl mx-auto">
      {error && (
        <div className="mb-4 p-4 bg-red-50 border-2 border-red-300 rounded-xl text-red-700 font-medium">
          ⚠️ {error}
        </div>
      )}
      
      {!file ? (
        <>
          {/* Main Upload Zone */}
          <div
            className={`relative border-4 border-dashed rounded-2xl p-12 text-center transition-all duration-200 shadow-xl ${
              dragActive 
                ? 'border-green-500 bg-gradient-to-br from-green-50 to-blue-50 scale-105' 
                : 'border-blue-300 bg-white hover:border-green-400 hover:bg-gradient-to-br hover:from-blue-50 hover:to-green-50'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              type="file"
              id="file-upload"
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleChange}
            />
            
            <div className="flex flex-col items-center space-y-4">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-green-500 flex items-center justify-center shadow-2xl">
                <Upload className="w-10 h-10 text-white" />
              </div>
              
              <div>
                <p className="text-2xl font-bold text-gray-900 mb-2">
                  Trascina qui il tuo contratto
                </p>
                <p className="text-lg text-gray-600">
                  oppure
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 w-full max-w-md">
                <label htmlFor="file-upload" className="flex-1">
                  <Button 
                    type="button"
                    className="w-full bg-gradient-to-r from-blue-600 to-green-500 hover:from-blue-700 hover:to-green-600 text-white font-bold text-lg px-6 py-6 shadow-xl hover:scale-105 transition-all"
                    onClick={() => document.getElementById('file-upload').click()}
                  >
                    <FileText className="w-5 h-5 mr-2" />
                    Scegli file
                  </Button>
                </label>

                <label htmlFor="camera-upload" className="flex-1">
                  <Button 
                    type="button"
                    className="w-full bg-gradient-to-r from-green-600 to-blue-500 hover:from-green-700 hover:to-blue-600 text-white font-bold text-lg px-6 py-6 shadow-xl hover:scale-105 transition-all"
                    onClick={() => document.getElementById('camera-upload').click()}
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Scatta foto
                  </Button>
                </label>
              </div>

              <input
                type="file"
                id="camera-upload"
                className="hidden"
                accept="image/*"
                capture="environment"
                onChange={handleChange}
              />

              <p className="text-sm text-gray-500 mt-2 font-medium">
                📄 PDF, JPG o PNG • Max 10MB • 📸 Scatta foto del contratto
              </p>
            </div>
          </div>

          {/* Mobile-specific tip */}
          <div className="mt-4 p-4 bg-green-50 border-2 border-green-200 rounded-xl md:hidden">
            <p className="text-sm text-green-800 text-center font-medium">
              💡 <strong>Su mobile?</strong> Usa "Scatta foto" per fotografare il contratto direttamente!
            </p>
          </div>
        </>
      ) : (
        <div className="border-4 border-green-500 rounded-2xl p-6 bg-gradient-to-br from-green-50 to-blue-50 shadow-2xl">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-green-500 flex items-center justify-center shadow-lg">
                <FileText className="w-7 h-7 text-white" />
              </div>
              <div>
                <p className="font-bold text-gray-900 text-lg">{file.name}</p>
                <p className="text-sm text-gray-600 font-medium">
                  {(file.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
            </div>
            {!uploading && (
              <button
                onClick={removeFile}
                className="text-gray-500 hover:text-red-600 transition-colors hover:scale-110"
              >
                <X className="w-6 h-6" />
              </button>
            )}
          </div>

          <Button 
            onClick={handleUpload}
            disabled={uploading}
            className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold text-lg py-6 shadow-xl disabled:opacity-50"
          >
            {uploading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Analisi in corso...
              </>
            ) : (
              <>
                <CheckCircle className="w-5 h-5 mr-2" />
                Analizza contratto
              </>
            )}
          </Button>
        </div>
      )}
    </div>

    {/* Payment Modal */}
    <PaymentModal
      isOpen={showPaymentModal}
      onClose={() => setShowPaymentModal(false)}
      contractId={uploadedContractId}
      fileName={file?.name}
    />
  </>
  );
};

export default UploadZone;
